import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../utils/siteSettings";
import usePropertyPins from "../../hooks/usePropertyPins";
import {
  PIN_STATUSES,
  pinColor,
  pinErrorMessage,
  pinLabel,
  pinPersonName,
  pinStatus,
} from "../../helpers/propertyPins";
import PinGlyph from "./PinGlyph";
import styles from "./PinStatusPicker.module.css";

/** "Oct 12, 2026" — US format whatever the viewer's machine locale is. */
export function formatPinDate(iso) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Who placed the pin and when, e.g. "Placed by Sam Seat · Oct 21, 2026". Pins are
 * the company's, so every seat may see who did what (the client asked for it on
 * 2026-10-22; elsewhere the UI still shows no names).
 */
export function pinPlacedLine(pin) {
  const when = formatPinDate(pin?.created_at);
  if (!pin?.created_by) return when ? `Placed ${when}` : "";
  return `Placed by ${pinPersonName(pin.created_by)}${when ? ` · ${when}` : ""}`;
}

/** Who moved it last, once it has been moved, e.g. "Changed by Ann Owner · Oct 22, 2026". */
export function pinChangedLine(pin) {
  if (!pin?.updated_at || !pin?.created_at || pin.updated_at === pin.created_at) return "";
  const when = formatPinDate(pin.updated_at);
  if (!pin.updated_by) return when ? `Changed ${when}` : "";
  return `Changed by ${pinPersonName(pin.updated_by)}${when ? ` · ${when}` : ""}`;
}

/** One line for tooltips: "Contacted · Placed by Sam Seat · Oct 21, 2026". */
export function pinSummary(pin) {
  return [pinLabel(pin), pinPlacedLine(pin), pinChangedLine(pin)].filter(Boolean).join(" · ");
}

/**
 * Contacted / Inspected / Contracted / Built for one property, shared by every
 * seat on the account. `propertyId` is the encoded id search results carry.
 *
 * A property has one pin, so once it is pinned only that pin shows, as a chip
 * that opens a menu of the four fixed pins (pins carry no text of their own),
 * plus an × to remove it; all four are offered as buttons only while there is
 * no pin yet.
 */
export default function PinStatusPicker({ propertyId, compact = false }) {
  const { byPropertyId, status: storeStatus, setStatus, remove } = usePropertyPins();
  const [saving, setSaving] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // The pin's menu starts on the current pin and closes on a click anywhere
  // else or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    menuRef.current?.querySelector('[aria-checked="true"]')?.focus();
    const onPointer = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // No login, or no active subscription: the feature isn't offered.
  if (!propertyId || storeStatus === "idle" || storeStatus === "unavailable") {
    return null;
  }

  const pin = byPropertyId.get(propertyId);
  const busy = saving !== null || storeStatus === "loading";

  const choose = async (key) => {
    if (busy) return;
    if (pin?.status === key) {
      setMenuOpen(false);
      return;
    }
    setSaving(key);
    try {
      await setStatus(propertyId, key);
      setMenuOpen(false);
      toast.success(`Marked ${pinStatus(key)?.label}`, TOAST_SETTINGS);
    } catch (error) {
      toast.error(pinErrorMessage(error), TOAST_SETTINGS);
    } finally {
      setSaving(null);
    }
  };

  const clear = async () => {
    if (busy || !pin) return;
    setSaving("remove");
    try {
      await remove(pin);
      setMenuOpen(false);
      toast.success("Pin removed", TOAST_SETTINGS);
    } catch (error) {
      toast.error(pinErrorMessage(error), TOAST_SETTINGS);
    } finally {
      setSaving(null);
    }
  };

  // The pin as a chip; a status this build doesn't know still shows.
  const current = pin
    ? pinStatus(pin.status) || { label: pinLabel(pin), color: pinColor(pin.status), textColor: "#fff" }
    : null;

  return (
    <div className={compact ? `${styles.picker} ${styles.compact}` : styles.picker}>
      <div className={styles.heading}>
        {/* Grey until the property is pinned, then the pin's own colour. */}
        <PinGlyph color={pin ? pinColor(pin.status) : "#9CA3AF"} height={15} className={styles.headingPin} />
        Job tracker
        {!pin && <span className={styles.hint}>Add a pin</span>}
      </div>
      <div className={styles.options} role="group" aria-label="Job tracker status">
        {!pin ? (
          PIN_STATUSES.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={false}
              disabled={busy}
              onClick={() => choose(option.key)}
              className={styles.option}
              style={{ borderColor: option.color, color: option.ink }}
            >
              <PinGlyph color={option.color} className={styles.optionPin} />
              {saving === option.key ? "Saving…" : option.label}
            </button>
          ))
        ) : (
          <>
            <div className={styles.pinMenuWrap} ref={menuRef}>
              {/* The pin itself is the control: click it to choose another pin. */}
              <button
                type="button"
                className={`${styles.option} ${styles.active} ${styles.current}`}
                style={{ backgroundColor: current.color, borderColor: current.color, color: current.textColor }}
                data-testid="pin-current-status"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`${current.label}, change pin`}
                disabled={busy}
                onClick={() => setMenuOpen((value) => !value)}
              >
                <PinGlyph
                  color={current.textColor}
                  holeColor={current.color}
                  outline={current.textColor}
                  className={styles.optionPin}
                />
                {saving && saving !== "remove" ? "Saving…" : current.label}
                <FiChevronDown
                  aria-hidden="true"
                  className={styles.chevron}
                  style={{ transform: menuOpen ? "rotate(180deg)" : "none" }}
                />
              </button>
              {menuOpen && (
                <div className={styles.pinMenu} role="menu" aria-label="Change pin" data-testid="pin-menu">
                  {PIN_STATUSES.map((option) => {
                    const checked = pin.status === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        role="menuitemradio"
                        aria-checked={checked}
                        className={styles.pinMenuItem}
                        disabled={busy}
                        onClick={() => choose(option.key)}
                      >
                        <PinGlyph color={option.color} height={14} className={styles.optionPin} />
                        <span>{option.label}</span>
                        {checked && <FiCheck aria-hidden="true" className={styles.pinMenuCheck} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              className={styles.iconButton}
              onClick={clear}
              disabled={busy}
              aria-label="Remove pin"
              aria-busy={saving === "remove"}
              title={saving === "remove" ? "Removing…" : "Remove pin"}
            >
              <FiX aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      {pin && (
        <div className={styles.meta} data-testid="pin-who">
          {pinPlacedLine(pin) && <div>{pinPlacedLine(pin)}</div>}
          {pinChangedLine(pin) && <div>{pinChangedLine(pin)}</div>}
        </div>
      )}
    </div>
  );
}

/** The property's pin as a small badge, for result cards. Nothing when unpinned. */
export function PinStatusBadge({ propertyId }) {
  const { byPropertyId } = usePropertyPins();
  const pin = propertyId ? byPropertyId.get(propertyId) : null;
  if (!pin) return null;

  const color = pinColor(pin.status);
  const textColor = pinStatus(pin.status)?.textColor || "#fff";
  return (
    <span
      className={`label_wrap ${styles.badge}`}
      style={{ backgroundColor: color, color: textColor }}
      title={`Job tracker: ${pinSummary(pin)}`}
      data-testid="pin-status-badge"
    >
      {/* The same pin as on the map, turned over on its colour like the picker chip. */}
      <PinGlyph color={textColor} holeColor={color} outline={textColor} height={14} className={styles.badgePin} />
      {pinLabel(pin)}
    </span>
  );
}
